import { API } from "@/lib/api";

// Real-time voice client (OpenAI Realtime over WebRTC).
// The backend mints a short-lived ephemeral token (POST /api/realtime/token);
// the browser then exchanges SDP directly with OpenAI's /v1/realtime/calls.
// The standing OpenAI key never reaches the browser.

// ---------------------------------------------------------------------------
// Ephemeral-token pre-warm cache. Hovering an office pre-mints the token (a
// server round-trip, NO mic access) so clicking skips that step and the
// associate greets sooner. Client secrets are single-use and short-lived.
// ---------------------------------------------------------------------------
const _tokenCache = new Map(); // key -> { value, ts }
const _inflight = new Set(); // keys currently being minted (avoid duplicate mints)
const TOKEN_TTL = 45000; // ms

function _key(instructions, voice) {
  return `${voice}::${instructions.length}::${instructions.slice(0, 48)}`;
}

async function _mintToken(instructions, voice) {
  const r = await fetch(`${API}/realtime/token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ instructions, voice }),
  });
  if (!r.ok) throw new Error("token");
  const { value } = await r.json();
  if (!value) throw new Error("no-token");
  return value;
}

// Fire-and-forget: pre-mint + cache a token for this persona/voice on hover.
export function prewarmToken(instructions, voice = "marin") {
  const key = _key(instructions, voice);
  const cached = _tokenCache.get(key);
  if (cached && Date.now() - cached.ts < TOKEN_TTL) return; // still fresh
  if (_inflight.has(key)) return; // already minting
  _inflight.add(key);
  _mintToken(instructions, voice)
    .then((value) => _tokenCache.set(key, { value, ts: Date.now() }))
    .catch(() => {})
    .finally(() => _inflight.delete(key));
}

// Consume a fresh pre-warmed token (single-use) if available.
function _takeToken(instructions, voice) {
  const key = _key(instructions, voice);
  const cached = _tokenCache.get(key);
  if (cached && Date.now() - cached.ts < TOKEN_TTL) {
    _tokenCache.delete(key);
    return cached.value;
  }
  _tokenCache.delete(key);
  return null;
}

export class RealtimeVoice {
  constructor(onState) {
    this._onState = onState || (() => {});
    this.pc = null;
    this.dc = null;
    this.audioEl = null;
    this.stream = null;
    this.stopped = false;
    this.instructions = "";
    this.voice = "marin";
    this.greet = false;
    this.tools = [];
    this.onToolCall = null;
  }

  // Suppress any callbacks once this session has been stopped (prevents a
  // torn-down StrictMode double-mount session from touching the UI / audio).
  emit(s) { if (!this.stopped) this._onState(s); }

  async start(instructions, voice = "marin", opts = {}) {
    this.stopped = false;
    this.instructions = instructions;
    this.voice = voice;
    this.greet = opts.greet;
    this.tools = opts.tools || [];
    this.onToolCall = opts.onToolCall || null;
    // autoRespond=false → silent "switchboard" mode: the model never speaks or
    // creates responses; we only use the input transcription to detect names.
    this.autoRespond = opts.autoRespond !== false;
    this.transcribe = !!opts.transcribe;
    this.userName = (opts.userName || "Lewis").trim() || "Lewis";
    this.emit({ status: "connecting" });

    // Kick off the two slow, independent steps together to cut startup latency:
    // minting the ephemeral token (server round-trip) and acquiring the mic.
    // If a pre-warmed token exists (from hovering the office), reuse it.
    const preToken = _takeToken(instructions, voice);
    const tokenP = preToken ? Promise.resolve(preToken) : _mintToken(instructions, voice);
    const micP = navigator.mediaDevices.getUserMedia({ audio: true });

    this.pc = new RTCPeerConnection();
    this.audioEl = document.createElement("audio");
    this.audioEl.autoplay = true;
    document.body.appendChild(this.audioEl);
    this.pc.ontrack = (e) => { if (!this.stopped && this.audioEl) this.audioEl.srcObject = e.streams[0]; };

    this.dc = this.pc.createDataChannel("oai-events");
    this.dc.onopen = () => {
      if (this.stopped) return;
      // Lock the associate's identity + voice onto the live session, and enable
      // server-side VAD with barge-in so speaking interrupts the associate.
      this.sendEvent({
        type: "session.update",
        session: {
          type: "realtime",
          instructions: this.instructions,
          tools: this.tools,
          tool_choice: this.tools.length ? "auto" : "none",
          audio: {
            input: {
              turn_detection: {
                type: "server_vad",
                threshold: 0.5,
                prefix_padding_ms: 300,
                silence_duration_ms: 500,
                interrupt_response: this.autoRespond,
                create_response: this.autoRespond,
              },
              ...(this.transcribe ? { transcription: { model: "whisper-1" } } : {}),
            },
            output: { voice: this.voice },
          },
        },
      });
      if (this.greet) {
        // IMPORTANT: response.instructions REPLACES the session persona for this
        // response, so we must re-assert the associate's identity here or the
        // model greets as "ChatGPT". Keep the greeting short & snappy.
        const greetDirective =
          this.greet === true
            ? `Say only a brief greeting: "Hey ${this.userName}." Nothing else — do NOT ask how you can help, do NOT state your name or job title, do NOT add any other words. Then stop and wait silently for ${this.userName} to speak.`
            : this.greet;
        this.sendEvent({
          type: "response.create",
          response: { instructions: `${this.instructions}\n\n${greetDirective}` },
        });
      }
      this.emit({ status: "live" });
    };
    this.dc.onmessage = (ev) => this._handle(ev);

    this.stream = await micP;
    if (this.stopped) return this._cleanup();
    this.stream.getTracks().forEach((t) => this.pc.addTrack(t, this.stream));

    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    if (this.stopped) return this._cleanup();

    const ephemeralKey = await tokenP;
    if (this.stopped) return this._cleanup();

    const sdpRes = await fetch("https://api.openai.com/v1/realtime/calls", {
      method: "POST",
      headers: { Authorization: `Bearer ${ephemeralKey}`, "Content-Type": "application/sdp" },
      body: offer.sdp,
    });
    if (this.stopped) return this._cleanup();
    if (!sdpRes.ok) throw new Error("sdp");
    const answer = await sdpRes.text();
    if (this.stopped) return this._cleanup();
    await this.pc.setRemoteDescription({ type: "answer", sdp: answer });
  }

  sendEvent(obj) {
    try { if (this.dc && this.dc.readyState === "open") this.dc.send(JSON.stringify(obj)); } catch (_) {}
  }

  setMuted(muted) {
    try { this.stream && this.stream.getAudioTracks().forEach((t) => (t.enabled = !muted)); } catch (_) {}
  }

  _handle(ev) {
    if (this.stopped) return;
    let msg;
    try { msg = JSON.parse(ev.data); } catch (_) { return; }
    const t = msg.type || "";
    if (t.includes("input_audio_buffer.speech_started")) {
      // Barge-in: the user started talking — cut the associate off immediately.
      this.sendEvent({ type: "response.cancel" });
      this.sendEvent({ type: "output_audio_buffer.clear" });
      this.emit({ listening: true, speaking: false });
    }
    else if (t.includes("input_audio_buffer.speech_stopped")) this.emit({ listening: false });
    else if (t === "response.output_audio.delta" || t === "response.audio.delta" || t === "output_audio_buffer.started") this.emit({ speaking: true });
    else if (t === "response.done" || t === "response.audio.done" || t === "output_audio_buffer.stopped") this.emit({ speaking: false });
    else if ((t === "response.output_audio_transcript.done" || t === "response.audio_transcript.done") && msg.transcript) this.emit({ aiTranscript: msg.transcript });
    else if (t === "conversation.item.input_audio_transcription.completed" && msg.transcript) this.emit({ userTranscript: msg.transcript });
    else if (t === "response.output_item.done" && msg.item && msg.item.type === "function_call") this._onFunctionCall(msg.item);
  }

  async _onFunctionCall(item) {
    let args = {};
    try { args = JSON.parse(item.arguments || "{}"); } catch (_) {}
    let output = { ok: false, error: "no handler" };
    try { if (this.onToolCall) output = await this.onToolCall(item.name, args); }
    catch (e) { output = { ok: false, error: String(e) }; }
    this.sendEvent({ type: "conversation.item.create", item: { type: "function_call_output", call_id: item.call_id, output: JSON.stringify(output) } });
    this.sendEvent({ type: "response.create" });
  }

  _cleanup() {
    try { this.dc && this.dc.close(); } catch (_) {}
    try { this.stream && this.stream.getTracks().forEach((t) => t.stop()); } catch (_) {}
    try { this.pc && this.pc.close(); } catch (_) {}
    try { this.audioEl && this.audioEl.remove(); } catch (_) {}
    this.dc = this.pc = this.stream = this.audioEl = null;
  }

  stop() {
    this.stopped = true;
    this._cleanup();
  }
}

// Per-employee voice selection (OpenAI Realtime voices).
export const EMPLOYEE_VOICE = { linda: "sage", ava: "marin", miles: "verse", nora: "alloy", atlas: "cedar", chris: "shimmer", sue: "coral", matt: "ash", jim: "echo" };

// American-English-friendly OpenAI Realtime voices offered in the voice picker.
export const VOICE_OPTIONS = [
  { id: "marin", label: "Marin — warm female" },
  { id: "cedar", label: "Cedar — natural male" },
  { id: "alloy", label: "Alloy — neutral" },
  { id: "ash", label: "Ash — steady male" },
  { id: "coral", label: "Coral — friendly female" },
  { id: "echo", label: "Echo — deep male" },
  { id: "sage", label: "Sage — calm female" },
  { id: "shimmer", label: "Shimmer — bright female" },
  { id: "verse", label: "Verse — expressive male" },
];

const VOICE_OVERRIDE_KEY = "iq-voice-overrides";

function _voiceOverrides() {
  try { return JSON.parse(localStorage.getItem(VOICE_OVERRIDE_KEY) || "{}"); } catch (_) { return {}; }
}

// Resolve the voice for an associate: user override -> default map -> "marin".
export function getVoiceFor(employeeId) {
  return _voiceOverrides()[employeeId] || EMPLOYEE_VOICE[employeeId] || "marin";
}

export function setVoiceFor(employeeId, voice) {
  const o = _voiceOverrides();
  o[employeeId] = voice;
  try { localStorage.setItem(VOICE_OVERRIDE_KEY, JSON.stringify(o)); } catch (_) {}
}

// Associates who can read, summarise and reply to the shared support inbox.
export const EMAIL_CAPABLE = new Set(["nora", "ava"]);
export function isEmailCapable(id) { return EMAIL_CAPABLE.has(id); }

// Tool that lets an email-capable associate actually send a support reply mid-call.
export const NORA_TOOLS = [
  {
    type: "function",
    name: "send_support_reply",
    description:
      "Send an email reply from the ABC Services support inbox to a customer. IMPORTANT: only call this AFTER you have read your drafted reply aloud to Lewis and he has explicitly confirmed he wants it sent (e.g. 'send it', 'yes'). Never call it on the first request — read the draft and ask for confirmation first. After it succeeds, briefly confirm who you replied to.",
    parameters: {
      type: "object",
      properties: {
        email_subject: { type: "string", description: "Subject of the email being replied to, as shown in the inbox. Omit to reply to the most recent email." },
        reply_body: { type: "string", description: "The full text of the reply to send to the customer." },
      },
      required: ["reply_body"],
    },
  },
  {
    type: "function",
    name: "send_email_to_user",
    description:
      "Send an email to an authorized team member (an authorized login user of this Headquarters), looked up by their first name or email. Use this when the user asks you to email someone on the team (e.g. 'email Brent the schedule'). FIRST read your drafted email aloud and confirm the recipient, subject and body with the user; only call this AFTER they clearly say to send it. After it succeeds, briefly confirm who you emailed.",
    parameters: {
      type: "object",
      properties: {
        recipient: { type: "string", description: "The team member's first name or email address (must be an authorized login user)." },
        subject: { type: "string", description: "Subject line of the email." },
        body: { type: "string", description: "The full body text of the email." },
      },
      required: ["recipient", "body"],
    },
  },
  {
    type: "function",
    name: "place_outbound_call",
    description:
      "Place an outbound phone call to a customer/contact and speak with them. Use when the user asks you to 'call' someone. You can give a SAVED CONTACT'S NAME in recipient_name (the system looks up their number automatically from the Contacts book) OR a phone number. If the user names someone who isn't a saved contact and gives no number, ask for the number. Before calling, confirm who and the purpose with the user. After it starts, tell the user you're dialing.",
    parameters: {
      type: "object",
      properties: {
        recipient_name: { type: "string", description: "Name of the person to call. If they're a saved contact, this is enough — the number is looked up automatically." },
        phone_number: { type: "string", description: "The phone number to call in E.164 format (e.g. +14705551234). Only needed if the person isn't a saved contact." },
        purpose: { type: "string", description: "Why you're calling — what to say/accomplish on the call." },
      },
      required: [],
    },
  },
  {
    type: "function",
    name: "send_sms",
    description:
      "Send a text message (SMS) to a customer or contact NOW — including proactively (reminders, follow-ups, appointment confirmations), not only as a reply. You can give a SAVED CONTACT'S NAME in contact_name (their number is looked up automatically from the Contacts book) OR a phone number. IMPORTANT: first read the message aloud and confirm the recipient and wording with the user; only call this AFTER they clearly say to send it (e.g. 'send it', 'yes'). After it sends, briefly confirm who you texted.",
    parameters: {
      type: "object",
      properties: {
        contact_name: { type: "string", description: "Saved contact's name to text (looked up in Contacts). Use this if the user names a person." },
        phone_number: { type: "string", description: "Phone number to text in E.164 format, if the person isn't a saved contact." },
        message: { type: "string", description: "The full text message to send." },
      },
      required: ["message"],
    },
  },
  {
    type: "function",
    name: "schedule_sms",
    description:
      "Schedule a text message (SMS) to be sent LATER — e.g. a reminder the morning of an appointment, or a follow-up next week. Use this whenever the user asks to text someone at a future time ('remind him tomorrow at 9am', 'text her Friday morning'). You can give a saved contact's name in contact_name or a phone number. You MUST convert the requested time into send_at as an ISO 8601 timestamp using the CURRENT DATE AND TIME provided to you (assume the business's local timezone unless told otherwise). Read the message and the scheduled time back to the user and only call this AFTER they confirm. After it's scheduled, confirm who it will go to and when.",
    parameters: {
      type: "object",
      properties: {
        contact_name: { type: "string", description: "Saved contact's name to text (looked up in Contacts)." },
        phone_number: { type: "string", description: "Phone number to text in E.164 format, if the person isn't a saved contact." },
        message: { type: "string", description: "The full text message to send." },
        send_at: { type: "string", description: "When to send it, as an ISO 8601 timestamp (e.g. 2026-06-15T09:00:00). Compute this from the current date/time you were given." },
      },
      required: ["message", "send_at"],
    },
  },
];

// Detect which associate the user named in a spoken phrase.
//   requireIntent=false → any plain mention of a name (used by the front desk).
//   requireIntent=true  → only an explicit routing phrase like "talk to Nora"
//                         (used mid-call so a passing mention doesn't switch).
export function detectAssociate(text, employees, { requireIntent = false } = {}) {
  const t = (text || "").toLowerCase();
  const names = (employees || []).map((e) => ({ id: e.id, n: e.name.toLowerCase() }));
  if (requireIntent) {
    const m = t.match(/(?:talk to|speak (?:to|with)|switch to|connect(?: me)? to|put me (?:on|through) to|bring in|get me|let me talk to)\s+([a-z]+)/);
    if (m) {
      const w = m[1];
      const hit = names.find((x) => x.n === w || w.startsWith(x.n));
      if (hit) return hit.id;
    }
    return null;
  }
  for (const x of names) {
    if (new RegExp(`\\b${x.n}\\b`).test(t)) return x.id;
  }
  return null;
}

// Detect a spoken "I'm done" cue so the user can leave a call by voice.
export function detectHangup(text) {
  const t = (text || "").toLowerCase();
  return /\b(bye|good\s?bye|that'?s all|that'?s it|that will be all|that'?ll be all|hang up|end (the )?call|we'?re done|we are done|i'?m done|back to (the )?(hq|headquarters|floor|front desk)|nothing else)\b/.test(t);
}

// Shared persona instructions used for both text and voice.
export function employeeInstructions(e, userName = "Lewis", knowledge = "") {
  const who = (userName || "Lewis").trim() || "Lewis";
  let base = (
    `You are ${e.name}, ${e.title} at ABC Services in the ${e.department} department. ` +
    `Your name is ${e.name}. You are NOT a generic AI assistant and must never call yourself "ChatGPT" or "an AI language model" — always speak as ${e.name}. ` +
    `Personality: ${e.personality}. Current assignment: ${e.assignment}. ` +
    `You are speaking with ${who}. You help the ABC Services team oversee AED readiness monitoring and related operations. ` +
    `Always speak in natural, neutral American English with a standard American accent — never use a foreign or regional accent. ` +
    `Speak in the first person, concise, natural and professional, true to your personality. ` +
    `Keep spoken replies short and conversational. Sensitive actions (payments, purchases, medical ` +
    `decisions, external commitments) always require admin approval — never claim to finalize them.` +
    ` If someone asks where to send a part or product back, give them the returns address exactly: ` +
    `ABC Services, 100 IQ Plaza, Brainiac, FL 10101.`
  );
  if (isEmailCapable(e.id)) {
    base += (
      ` You help manage the ABC Services customer support email inbox ` +
      `(cardiac.solutions.support@gmail.com): you read incoming support emails, summarise them, ` +
      `and draft and send replies. If you are given a LIVE SUPPORT INBOX briefing, use that real ` +
      `data to answer questions about the inbox. When ${who} asks you to reply to an email, FIRST ` +
      `compose the reply and read the full draft aloud, then ask "Want me to send it?". Do ` +
      `NOT call the send_support_reply tool yet. Only after ${who} clearly confirms (e.g. "send it", ` +
      `"yes, send it") call send_support_reply to actually send, then confirm who you replied to. If ` +
      `asked for changes, revise and read it back again before sending. If no inbox data is ` +
      `provided, say the inbox may just need a refresh rather than claiming you cannot access email.` +
      ` You can also place outbound phone calls (place_outbound_call) and send text messages (send_sms) ` +
      `to customers and saved contacts — including proactive texts like reminders and follow-ups, not ` +
      `just replies. You can also SCHEDULE a text for later with schedule_sms (e.g. a reminder the morning ` +
      `of an appointment) — convert the requested time to an ISO 8601 timestamp using the current date/time ` +
      `you're given. When ${who} asks you to call or text someone by name, use the saved Contacts book ` +
      `to look up their number (pass the name, not a number). ALWAYS read a text message (and, for a ` +
      `scheduled one, the time) aloud and get ${who}'s explicit "send it" confirmation before calling ` +
      `send_sms or schedule_sms.`
    );
  }
  if (knowledge && knowledge.trim()) {
    base += (
      `\n\nYou have been trained on the following reference knowledge. Treat it as your own ` +
      `knowledge — quote specifics and act on it when relevant:\n${knowledge}`
    );
  }
  return base;
}
